import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PawPrint, Pencil, Trash2, MessageCircle, Siren, ScanLine, Lightbulb, Plus } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner } from "@/components/kit";
import { timeAgo } from "@/lib/files";
import DogFormDialog from "@/components/DogFormDialog";

export default function DogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dog, setDog] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [scans, setScans] = useState([]);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [savingTip, setSavingTip] = useState(null);

  const saveTipAsReminder = async (tip, idx) => {
    setSavingTip(idx);
    try {
      const due = new Date();
      due.setDate(due.getDate() + 7);
      const due_date = due.toISOString().slice(0, 10);
      await apiFetch("/reminders", {
        method: "POST",
        body: JSON.stringify({
          dog_id: id,
          type: "care",
          title: tip.slice(0, 120),
          due_date,
          recurrence: "none",
          notes: "Saved from breed care tips",
        }),
      });
      toast.success("Added to reminders · due in 7 days", {
        action: { label: "View", onClick: () => navigate("/app/reminders") },
      });
    } catch (err) {
      if (err.status === 402) {
        toast.error("Reminders are a Wooflect Pro feature");
        navigate("/app/subscription?highlight=pawpal_pro");
      } else {
        toast.error(err.message || "Could not save reminder");
      }
    } finally {
      setSavingTip(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, s, sc] = await Promise.all([
        apiFetch(`/dogs/${id}`),
        apiFetch("/chat/sessions").catch(() => []),
        apiFetch(`/scan/history?dog_id=${id}`).catch(() => []),
      ]);
      setDog(d);
      setSessions((s || []).filter((x) => x.dog_id === id));
      setScans(sc || []);
    } catch {
      toast.error("Could not load dog");
      navigate("/app");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dog?.breed) return;
    setTipsLoading(true);
    apiFetch(`/breed-tips?breed=${encodeURIComponent(dog.breed)}`)
      .then((r) => setTips(r.tips || []))
      .catch(() => setTips([]))
      .finally(() => setTipsLoading(false));
  }, [dog?.breed]);

  const del = async () => {
    if (!window.confirm(`Delete ${dog.name}'s profile? This can't be undone.`)) return;
    try {
      await apiFetch(`/dogs/${id}`, { method: "DELETE" });
      toast.success("Profile deleted");
      navigate("/app");
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  if (loading) return <FullPageSpinner />;
  if (!dog) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title={dog.name}
        backTo="/app"
        right={
          <div className="flex gap-2">
            <button data-testid="dog-edit-btn" onClick={() => setEdit(true)} className="h-10 px-4 rounded-full bg-[#F2EFE9] flex items-center gap-1.5 font-bold text-sm text-[#2D2B2A] hover:bg-[#E8E3DA]">
              <Pencil size={16} /> Edit
            </button>
            <button data-testid="dog-delete-btn" onClick={del} className="h-10 w-10 rounded-full bg-[#FDECEB] flex items-center justify-center hover:brightness-95">
              <Trash2 size={17} color="#D9534F" />
            </button>
          </div>
        }
      />

      <div className="rounded-3xl overflow-hidden border border-[#E8E3DA] bg-white">
        <div className="h-52 bg-[#F2EFE9] flex items-center justify-center">
          {dog.photo_base64 ? (
            <img src={dog.photo_base64} alt={dog.name} className="h-full w-full object-cover" />
          ) : (
            <PawPrint size={60} color="#C4BFB8" />
          )}
        </div>
        <div className="px-5 pt-4 text-center">
          <p className="font-display text-lg font-extrabold text-[#2D2B2A]" data-testid="dog-summary">
            {dog.name} · {dog.breed || "Mixed breed"}
            {dog.weight ? `, ${dog.weight} ${dog.weight_unit === "kg" ? "kg" : "lbs"}` : ""}
          </p>
        </div>
        <div className="p-5 grid grid-cols-3 gap-3 text-center">
          <Stat label="Breed" value={dog.breed || "—"} />
          <Stat label="Age" value={dog.age ? `${dog.age} yr` : "—"} />
          <Stat label="Weight" value={dog.weight ? `${dog.weight} ${dog.weight_unit === "kg" ? "kg" : "lbs"}` : "—"} />
        </div>
        {dog.notes && (
          <div className="px-5 pb-5">
            <p className="text-sm font-bold text-[#9A938B] mb-1">Notes</p>
            <p className="text-[#4A4746]">{dog.notes}</p>
          </div>
        )}
      </div>

      <Link to="/app/chat" data-testid="dog-ask-ai-btn">
        <div className="flex items-center justify-center gap-2 rounded-full bg-[#E86A45] text-white font-extrabold py-3.5 mt-5 hover:bg-[#d65f3c] transition-colors">
          <MessageCircle size={18} /> Ask AI about {dog.name}
        </div>
      </Link>

      {/* Breed care tips */}
      {dog.breed && (
        <div className="mt-6 rounded-3xl border border-[#E8E3DA] bg-[#FBF6EE] p-5" data-testid="breed-tips">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={20} color="#E8A33D" />
            <h2 className="font-display text-lg font-extrabold">Breed care tips</h2>
            <span className="text-xs font-bold text-[#9A938B]">· {dog.breed}</span>
          </div>
          {tipsLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-4 rounded bg-[#EDE6DA] animate-pulse" style={{ width: `${90 - i * 12}%` }} />
              ))}
            </div>
          ) : tips && tips.length > 0 ? (
            <ul className="space-y-2.5">
              {tips.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#2D2B2A]" data-testid={`breed-tip-${i}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#E8A33D] shrink-0" />
                  <span className="flex-1">{t}</span>
                  <button
                    data-testid={`save-tip-${i}`}
                    onClick={() => saveTipAsReminder(t, i)}
                    disabled={savingTip === i}
                    className="shrink-0 flex items-center gap-1 rounded-full bg-white border border-[#E8D9BE] text-[#B5852B] font-bold text-xs px-2.5 py-1 hover:bg-[#FBF3E3] disabled:opacity-50"
                    title="Save as a care reminder"
                  >
                    <Plus size={13} /> Reminder
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#9A938B]">Tips aren&apos;t available right now — check back soon.</p>
          )}
          <p className="text-xs text-[#9A938B] mt-3">Tap <b>+ Reminder</b> on any tip to save it as a care reminder (due in 7 days). General breed guidance — always defer to your vet for {dog.name}.</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-8 mb-3">
        <h2 className="font-display text-xl font-extrabold">Health scans</h2>
        <Link to="/app/scan" data-testid="dog-new-scan-btn" className="text-sm font-bold text-[#6F8F72] flex items-center gap-1">
          <ScanLine size={16} /> New scan
        </Link>
      </div>
      {scans.length === 0 ? (
        <p className="text-[#9A938B]">No scans for {dog.name} yet. Use Snap &amp; Scan to check a symptom.</p>
      ) : (
        <div className="space-y-2">
          {scans.map((sc) => {
            const color = { emergency: "#D9534F", caution: "#B7791F", ok: "#4E7150", unknown: "#9A938B" }[sc.status] || "#9A938B";
            return (
              <div key={sc.id} data-testid={`dog-scan-${sc.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#E8E3DA]">
                {sc.image_base64 && (
                  <img src={sc.image_base64.startsWith("data:") ? sc.image_base64 : `data:image/jpeg;base64,${sc.image_base64}`} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{sc.title}</p>
                  <p className="text-xs font-bold" style={{ color }}>{sc.level_label}</p>
                  <p className="text-sm text-[#4A4746] truncate">{sc.summary}</p>
                </div>
                <span className="text-xs text-[#9A938B] shrink-0">{timeAgo(sc.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="font-display text-xl font-extrabold mt-8 mb-3">Health conversations</h2>
      {sessions.length === 0 ? (
        <p className="text-[#9A938B]">No conversations about {dog.name} yet.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <Link key={s.id} to={`/app/chat/${s.id}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8E3DA] hover:shadow-sm">
              {s.is_emergency ? <Siren size={18} color="#D9534F" /> : <MessageCircle size={18} color="#E86A45" />}
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{s.title}</p>
                <p className="text-sm text-[#4A4746] truncate">{s.last_message}</p>
              </div>
              <span className="text-xs text-[#9A938B]">{timeAgo(s.updated_at)}</span>
            </Link>
          ))}
        </div>
      )}

      <DogFormDialog open={edit} onClose={() => setEdit(false)} dog={dog} onSaved={load} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#F2EFE9] py-3">
      <p className="text-xs font-bold text-[#9A938B] uppercase">{label}</p>
      <p className="font-extrabold text-[#2D2B2A] mt-0.5 truncate px-1">{value}</p>
    </div>
  );
}
